export function pathMatchesPrefix(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

function getAllowedInternalToolKeys(user) {
  const allowedToolKeys = user?.allowedInternalToolKeys

  if (allowedToolKeys === null || allowedToolKeys === undefined) {
    return null
  }

  return Array.isArray(allowedToolKeys) ? allowedToolKeys : []
}

export function isRestrictedInternalUser(user) {
  return getAllowedInternalToolKeys(user) !== null
}

function isToolVisibleToUser(tool, user) {
  if (tool.corporateAllowed && user?.isCorporateUser) {
    return true
  }

  if (tool.readOnlyAllowed && user?.isReadOnlyLogin) {
    return true
  }

  if (tool.adminOnly && !user?.isAdmin) {
    return false
  }

  if (tool.hideForReadOnly && user?.isReadOnlyLogin) {
    return false
  }

  return true
}

export function getVisibleInternalTools(tools, user) {
  const allowedToolKeys = getAllowedInternalToolKeys(user)
  const visibleTools = tools.filter((tool) => isToolVisibleToUser(tool, user))

  if (allowedToolKeys === null) {
    return visibleTools
  }

  const allowedKeys = new Set(allowedToolKeys)
  return visibleTools.filter(
    (tool) => (tool.corporateAllowed && user?.isCorporateUser) || allowedKeys.has(tool.key),
  )
}

export function getVisibleInternalToolCategories(tools, categories) {
  const visibleCategories = new Set(tools.map((tool) => tool.category).filter(Boolean))
  return categories.filter((category) => visibleCategories.has(category))
}

export function filterInternalToolsByCategory(tools, category = 'All') {
  if (category === 'All') return tools

  return tools.filter((tool) => tool.category === category)
}

export function sortInternalToolsByCategory(tools, categories) {
  const categoryOrder = new Map(categories.map((category, index) => [category, index]))

  return [...tools].sort((firstTool, secondTool) => {
    const firstCategoryIndex = categoryOrder.get(firstTool.category) ?? categories.length
    const secondCategoryIndex = categoryOrder.get(secondTool.category) ?? categories.length
    const categoryDifference = firstCategoryIndex - secondCategoryIndex

    if (categoryDifference) return categoryDifference

    return firstTool.title.localeCompare(secondTool.title, 'en', { sensitivity: 'base' })
  })
}

export function getInternalToolKeyForRoute(pathname, tools) {
  const pathOnly = pathname.split('?')[0]

  for (const tool of tools) {
    const prefixes = tool.routePrefixes?.length ? tool.routePrefixes : [tool.href]

    if (prefixes.some((prefix) => pathMatchesPrefix(pathOnly, prefix))) {
      return tool.key
    }
  }

  return null
}

export function hasInternalToolRouteAccess(pathname, tools, user) {
  const allowedToolKeys = getAllowedInternalToolKeys(user)
  const toolKey = getInternalToolKeyForRoute(pathname, tools)
  const tool = tools.find((item) => item.key === toolKey)

  if (tool && !isToolVisibleToUser(tool, user)) {
    return false
  }

  if (allowedToolKeys === null) {
    return true
  }

  if (toolKey === null) {
    return false
  }

  return !!(tool?.corporateAllowed && user?.isCorporateUser) || allowedToolKeys.includes(toolKey)
}
